// src/App.js
import React from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useSearchParams,
} from 'react-router-dom';
import { Auth0Provider } from '@auth0/auth0-react';
import { ConfigProvider } from 'antd';
import LoginPage from './pages/Login/LoginPage';
import GalleryPage from './pages/Gallery/GalleryPage';
import TemplateFormPage from './pages/Templates/TemplateFormPage';
import ProtectedRoute from './components/ProtectedRoute';
import AccessDeniedPage from './pages/AccessDenied/AccessDeniedPage';
import AICoachSetupPage from './pages/AICoachSetup';
import BuyerProfilesPage from './pages/BuyerProfiles/BuyerProfilesPage';
import AuthGate from './components/AuthGate';
import { useAuth0 } from '@auth0/auth0-react';
import AxiosWithAuth from './context/AxiosWithAuth';
import { VendorProvider } from './context/VendorContext';
import { isLocal } from './services/api';
import './App.css';

const domain = process.env.REACT_APP_AUTH0_DOMAIN;
const clientId = process.env.REACT_APP_AUTH0_CLIENT_ID;
const audience = process.env.REACT_APP_AUTH0_AUDIENCE;
const scope = process.env.REACT_APP_AUTH0_SCOPE;

function RootRoute() {
  const { isAuthenticated, isLoading } = useAuth0();
  const [searchParams] = useSearchParams();
  const error = searchParams.get('error');
  const errorDescription = searchParams.get('error_description');

  // Handle access_denied error - show Access Denied page
  if (error === 'access_denied') {
    return <AccessDeniedPage />;
  }

  if (error) {
    console.error('Auth0 error:', error, errorDescription);
    return <Navigate to="/login" replace />;
  }

  if (isLoading) return null;
  if (isAuthenticated) return <Navigate to="/roleplays" replace />;
  return <Navigate to="/login" replace />;
}

function App() {
  const redirectUri = window.location.origin;

  return (
    <Auth0Provider
      domain={domain}
      clientId={clientId}
      authorizationParams={{
        redirect_uri: redirectUri,
        audience: audience,
        scope: scope,
      }}
      cacheLocation={isLocal ? 'localstorage' : 'memory'}
      useRefreshTokens={false}
      onRedirectError={(error) => {
        console.error('Auth0 redirect error:', error);
      }}
    >
      <ConfigProvider
        theme={{
          token: {
            colorPrimary: '#1890ff',
            borderRadius: 8,
          },
        }}
      >
        <Router>
          <AxiosWithAuth>
            <VendorProvider>
            <Routes>
              <Route path="/" element={<RootRoute />} />

              {/* LOGIN PAGE */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/ai-coach-setup" element={<AICoachSetupPage />} />

              {/* ACCESS DENIED PAGE */}
              <Route path="/access-denied" element={<AccessDeniedPage />} />

              {/* PROTECTED ROUTES - Wrapped with AuthGate */}
              <Route
                path="/roleplays"
                element={
                  <ProtectedRoute>
                    <AuthGate>
                      <GalleryPage />
                    </AuthGate>
                  </ProtectedRoute>
                }
              />
              <Route path="/gallery" element={<Navigate to="/roleplays" replace />} />

              <Route
                path="/templates/create"
                element={
                  <ProtectedRoute>
                    <AuthGate>
                      <TemplateFormPage />
                    </AuthGate>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/templates/edit/:id"
                element={
                  <ProtectedRoute>
                    <AuthGate>
                      <TemplateFormPage />
                    </AuthGate>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/roleplays/new"
                element={
                  <ProtectedRoute>
                    <AuthGate>
                      <AICoachSetupPage />
                    </AuthGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/roleplays/edit/:scenarioId"
                element={
                  <ProtectedRoute>
                    <AuthGate>
                      <AICoachSetupPage />
                    </AuthGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/roleplays/buyer-profiles"
                element={
                  <ProtectedRoute>
                    <AuthGate>
                      <BuyerProfilesPage />
                    </AuthGate>
                  </ProtectedRoute>
                }
              />
              <Route path="/agentCreation" element={<Navigate to="/roleplays/new" replace />} />
              <Route path="/coach" element={<Navigate to="/roleplays/new" replace />} />
              {/* 404 fallback */}
              <Route path="*" element={<Navigate to="/login" replace />} />
            </Routes>
            </VendorProvider>
          </AxiosWithAuth>
        </Router>
      </ConfigProvider>
    </Auth0Provider>
  );
}

export default App;
